import React, { useState, useEffect } from "react";
import { CreditCard, CheckCircle, Clock, XCircle, AlertCircle, Search, Download } from "lucide-react";
import AppLayout from "../../components/AppLayout";
import { api } from "../../api/client";

const STATUS_META = {
  paid:      { label: "Paid",      icon: CheckCircle,  cls: "bg-green-500/10 text-green-400 border-green-500/20" },
  initiated: { label: "Pending",   icon: Clock,        cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  open:      { label: "Open",      icon: Clock,        cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  failed:    { label: "Failed",    icon: XCircle,      cls: "bg-red-500/10 text-red-400 border-red-500/20" },
  refunded:  { label: "Refunded",  icon: AlertCircle,  cls: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.initiated;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${meta.cls}`}>
      <Icon size={11} /> {meta.label}
    </span>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function AdminPayments() {
  const [txns,    setTxns]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [query,   setQuery]   = useState("");
  const [filter,  setFilter]  = useState("all");

  useEffect(() => {
    api.get("/admin/payments")
      .then(setTxns)
      .catch(() => setTxns([]))
      .finally(() => setLoading(false));
  }, []);

  const totals = {
    revenue:  txns.filter((t) => t.payment_status === "paid").reduce((s, t) => s + (t.amount || 0), 0),
    paid:     txns.filter((t) => t.payment_status === "paid").length,
    pending:  txns.filter((t) => ["initiated", "open"].includes(t.payment_status)).length,
    refunded: txns.filter((t) => t.payment_status === "refunded").length,
  };

  const filtered = txns.filter((t) => {
    const matchQ = !query || [t.user_name, t.user_email, t.course_title, t.session_id]
      .some((v) => (v || "").toLowerCase().includes(query.toLowerCase()));
    const matchF = filter === "all" || t.payment_status === filter || (filter === "pending" && ["initiated", "open"].includes(t.payment_status));
    return matchQ && matchF;
  });

  const exportCsv = () => {
    const rows = [
      ["Date", "Customer", "Email", "Course", "Amount (£)", "Status", "Session ID"],
      ...filtered.map((t) => [
        fmt(t.created_at), t.user_name, t.user_email,
        t.course_title, t.amount?.toFixed(2), t.payment_status, t.session_id,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `bmg-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <AppLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-grape mb-1">Finance</p>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight">Payments</h1>
            <p className="text-sm text-gray-400 mt-2">Full transaction audit log — every checkout attempt recorded</p>
          </div>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 text-sm font-semibold hover:bg-white/5 transition-colors shrink-0">
            <Download size={14} /> Export CSV
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total Revenue",    value: `£${totals.revenue.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`, accent: "text-lime" },
            { label: "Paid",             value: totals.paid,     accent: "text-green-400" },
            { label: "Pending",          value: totals.pending,  accent: "text-yellow-400" },
            { label: "Refunded",         value: totals.refunded, accent: "text-purple-400" },
          ].map((s) => (
            <div key={s.label} className="bg-ink-700 rounded-2xl border border-white/5 p-5">
              <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">{s.label}</p>
              <p className={`text-3xl font-black mt-1 ${s.accent}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by customer, course, or session ID…"
              className="w-full bg-ink-700 border border-white/5 rounded-2xl pl-10 pr-4 py-3 text-sm text-white focus:outline-none focus:border-grape/40 placeholder-gray-600"
            />
          </div>
          <div className="flex gap-2">
            {["all", "paid", "pending", "refunded"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2.5 rounded-full text-xs font-bold capitalize transition-colors ${
                  filter === f ? "bg-grape text-white" : "bg-ink-700 border border-white/5 text-gray-400 hover:text-white"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-ink-700 rounded-3xl border border-white/5 overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-20 text-gray-500">Loading transactions…</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center py-20 text-gray-600">
              <CreditCard size={32} className="mb-3 opacity-40" />
              <p>{txns.length === 0 ? "No transactions yet — payments will appear here after the first checkout." : "No transactions match your search."}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    {["Date", "Customer", "Course", "Amount", "Status", "Session ID"].map((h) => (
                      <th key={h} className="px-5 py-4 text-left text-xs font-bold uppercase tracking-widest text-gray-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => (
                    <tr key={t.session_id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                      <td className="px-5 py-4 text-gray-400 whitespace-nowrap">{fmt(t.created_at)}</td>
                      <td className="px-5 py-4">
                        <p className="font-semibold text-white">{t.user_name}</p>
                        <p className="text-xs text-gray-500">{t.user_email}</p>
                      </td>
                      <td className="px-5 py-4 text-gray-300 max-w-[200px] truncate">{t.course_title}</td>
                      <td className="px-5 py-4 font-bold text-white whitespace-nowrap">£{(t.amount || 0).toFixed(2)}</td>
                      <td className="px-5 py-4"><StatusBadge status={t.payment_status} /></td>
                      <td className="px-5 py-4 font-mono text-xs text-gray-600 truncate max-w-[140px]" title={t.session_id}>{t.session_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-600 text-center">
          All transactions are stored in MongoDB for GDPR audit purposes. User deletions preserve payment records with anonymised data.
        </p>
      </div>
    </AppLayout>
  );
}
